import type { DpiProfile } from "../../../../../mouse-management/src/pages/dpiSetting/types";
import type { TFunction } from "i18next";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import {
  getSupportedDevicesSync,
  resolveCatalogRowFromSnapshot,
} from "@eweadn-qiankun/shared-utils";
import { normalizeMouseChangeKeyDisplayLabel } from "../../../utils/jiangmengMouseKeyLabel";
import { resolveZhanliMouseKeyLabel } from "../../../../../main-app/src/sdkApi/mouse/zhanli/keymapUi";
import {
  buildZhanliMacroNameResolver,
  readZhanliMacrosFromBoardSnapshot,
} from "../../../../../main-app/src/sdkApi/mouse/zhanli/keymap";

export type FreqLinkScheme = "a" | "c";

/** 社区预览：优先板载快照 linkScheme，其次设备表 freqLinkHint */
export function resolveFreqLinkSchemeForPreview(
  meta: CommunityPreviewDeviceMeta,
  configData?: Record<string, unknown>
): FreqLinkScheme {
  const snapshot = configData?._boardSnapshot;
  if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) {
    const linkScheme = (snapshot as { linkScheme?: string }).linkScheme;
    if (linkScheme === "a" || linkScheme === "c") return linkScheme;
  }

  const catalog =
    resolveCatalogRowFromSnapshot(meta.deviceCatalog ?? null, "mouse") ??
    (getSupportedDevicesSync().mouse as Array<Record<string, unknown>>).find(
      (row) => String(row.deviceId ?? "") === meta.mouseDeviceId
    );

  if (catalog) {
    const sol = String(catalog.solutionRovider ?? "").toLowerCase();
    if (sol === "freqc") return "c";
    if (catalog.freqLinkHint === "c") return "c";
  }

  return "a";
}

export interface FreqParameterPreviewValues {
  sleepMinutes: number;
  scrollFlag: number;
  straightCorrection: boolean;
}

export function getFreqParameterPreviewValues(
  configData?: Record<string, unknown>
): FreqParameterPreviewValues {
  const perf = asRecord(configData?.performance) ?? {};
  const sensor = asRecord(configData?.sensor) ?? {};

  return {
    sleepMinutes: Number(
      perf.sleepMinutes ?? perf.freqSleepLight ?? perf.sleepTime ?? 10
    ),
    scrollFlag: Number(perf.freqScrollFlag ?? perf.scrollFlag ?? 0),
    straightCorrection: Boolean(
      perf.angle ?? sensor.angle ?? configData?.angle
    ),
  };
}

const DEFAULT_DPI_PROFILES: DpiProfile[] = [
  {
    id: 0,
    enabled: true,
    xValue: 800,
    yValue: 800,
    color: { r: 0, g: 200, b: 83 },
    xyIndependent: false,
    label: "DPI 1",
  },
  {
    id: 1,
    enabled: true,
    xValue: 1600,
    yValue: 1600,
    color: { r: 33, g: 150, b: 243 },
    xyIndependent: false,
    label: "DPI 2",
  },
];

export interface MouseTriggerSidePreview {
  triggerPoint: number;
  fastTriggerState: number;
  fastTrigger: number;
  tactileFeedback: number;
  buttonSound: number;
}

export interface MouseTriggerPreviewValues {
  left: MouseTriggerSidePreview;
  right: MouseTriggerSidePreview;
  selectedButton: "left" | "right" | "both";
}

export interface MousePerformancePreviewValues {
  reportRate: number;
  sensorMode: 0 | 1 | 256;
  /** 贝盈：0 办公 / 1 高速 / 2 游戏 / 3 狂暴 */
  workMode: number;
}

export interface MouseChangeKeyPreviewLabels {
  left?: string;
  middle?: string;
  right?: string;
  side1?: string;
  side2?: string;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * 匠盟旧社区包可能把 EEPROM raw（如 7）写进 xValue；人类 DPI 为 (raw+1)*50。
 * 已是 50 步进的人类值则原样返回。仅用于匠盟。
 */
function coerceJiangmengCommunityDpiValue(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 800;
  if (v >= 50 && v % 50 === 0) return v;
  return (Math.floor(v) + 1) * 50;
}

function isJiangmengCommunityPayload(
  configData?: Record<string, unknown>
): boolean {
  const provider = String(configData?._boardProvider ?? "").toLowerCase();
  if (provider === "jiangmeng") return true;
  const snap = asRecord(configData?._boardSnapshot);
  return String(snap?.format ?? "") === "jiangmeng-board-v1";
}

export function getMouseDpiProfiles(configData?: Record<string, unknown>): {
  profiles: DpiProfile[];
  activeIndex: number;
} {
  const dpi = asRecord(configData?.dpi);
  const jiangmeng = isJiangmengCommunityPayload(configData);
  let rawProfiles = dpi?.profiles;

  // 无 profiles 时，从匠盟 snapshot.config.dpis 回退
  if (jiangmeng && (!Array.isArray(rawProfiles) || rawProfiles.length === 0)) {
    const snap = asRecord(configData?._boardSnapshot);
    const cfg = asRecord(snap?.config);
    const dpis = cfg?.dpis;
    if (Array.isArray(dpis) && dpis.length > 0) {
      rawProfiles = dpis.map((item, index) => {
        const p = asRecord(item) ?? {};
        const human = coerceJiangmengCommunityDpiValue(
          Number(p.xValue ?? p.value ?? 800)
        );
        const humanY = coerceJiangmengCommunityDpiValue(
          Number(p.yValue ?? p.value ?? human)
        );
        return {
          id: Number(p.id ?? p.index ?? index),
          enabled: true,
          xValue: human,
          yValue: humanY,
          color: p.color,
          xyIndependent: human !== humanY,
          label: `DPI ${index + 1}`,
        };
      });
    }
  }

  if (!Array.isArray(rawProfiles) || rawProfiles.length === 0) {
    return { profiles: DEFAULT_DPI_PROFILES, activeIndex: 0 };
  }

  const profiles = rawProfiles.map((item, index) => {
    const p = asRecord(item) ?? {};
    const color = asRecord(p.color);
    const xRaw = Number(p.xValue ?? p.x ?? p.value ?? 800);
    const yRaw = Number(p.yValue ?? p.y ?? xRaw);
    let r = 0;
    let g = 200;
    let b = 83;
    if (color) {
      r = Number(color.r ?? 0);
      g = Number(color.g ?? 200);
      b = Number(color.b ?? 83);
    } else if (typeof p.color === "string") {
      const m = p.color.match(/\d+/g);
      if (m && m.length >= 3) {
        r = Number(m[0]);
        g = Number(m[1]);
        b = Number(m[2]);
      }
    }
    const xValue = jiangmeng ? coerceJiangmengCommunityDpiValue(xRaw) : xRaw;
    const yValue = jiangmeng ? coerceJiangmengCommunityDpiValue(yRaw) : yRaw;
    return {
      id: Number(p.id ?? index),
      enabled: Boolean(p.enabled ?? true),
      xValue: Number.isFinite(xValue) && xValue > 0 ? xValue : 800,
      yValue: Number.isFinite(yValue) && yValue > 0 ? yValue : xValue || 800,
      color: { r, g, b },
      xyIndependent: Boolean(p.xyIndependent ?? false),
      label: String(p.label ?? `DPI ${index + 1}`),
    } satisfies DpiProfile;
  });

  return {
    profiles,
    activeIndex: Number(dpi?.activeIndex ?? 0),
  };
}

function readTriggerDeviceValue(raw: unknown, fallback: number): number {
  const nested = asRecord(raw);
  if (nested && nested.triggerPoint != null) {
    const point = Number(nested.triggerPoint);
    return Number.isFinite(point) ? point : fallback;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

function readTriggerSidePreview(
  raw: unknown,
  fallbackPoint: number
): MouseTriggerSidePreview {
  const nested = asRecord(raw);
  if (!nested) {
    const point = readTriggerDeviceValue(raw, fallbackPoint);
    return {
      triggerPoint: point,
      fastTriggerState: 1,
      fastTrigger: 0,
      tactileFeedback: 0,
      buttonSound: 0,
    };
  }
  return {
    triggerPoint: readTriggerDeviceValue(nested, fallbackPoint),
    fastTriggerState: Number(nested.fastTriggerState ?? 1) ? 1 : 0,
    fastTrigger: Number(nested.fastTrigger ?? 0),
    tactileFeedback: Number(nested.tactileFeedback ?? 0),
    buttonSound: Number(nested.buttonSound ?? 0) ? 1 : 0,
  };
}

function defaultTriggerDeviceValue(deviceId?: string): number {
  if (String(deviceId ?? "").toLowerCase() === "alpha6") {
    return 4;
  }
  return 5;
}

export function getMouseTriggerValues(
  configData?: Record<string, unknown>,
  deviceId?: string
): MouseTriggerPreviewValues {
  const trigger = asRecord(configData?.trigger);
  const selected = trigger?.selectedButton;
  const fallback = defaultTriggerDeviceValue(deviceId);
  return {
    left: readTriggerSidePreview(trigger?.left, fallback),
    right: readTriggerSidePreview(trigger?.right, fallback),
    selectedButton:
      selected === "left" || selected === "right" || selected === "both"
        ? selected
        : "both",
  };
}

export function getMouseChangeKeyLabels(
  configData?: Record<string, unknown>
): MouseChangeKeyPreviewLabels {
  const changeKey = asRecord(configData?.changeKey);
  if (!changeKey) return {};
  return {
    left: typeof changeKey.left === "string" ? changeKey.left : undefined,
    middle: typeof changeKey.middle === "string" ? changeKey.middle : undefined,
    right: typeof changeKey.right === "string" ? changeKey.right : undefined,
    side1: typeof changeKey.side1 === "string" ? changeKey.side1 : undefined,
    side2: typeof changeKey.side2 === "string" ? changeKey.side2 : undefined,
  };
}

/** 改键页 Mouse 组件用的 buttonMappings */
export function getMouseButtonMappings(
  configData: Record<string, unknown> | undefined,
  labels: {
    left: string;
    middle: string;
    right: string;
    side1: string;
    side2: string;
  },
  t?: TFunction,
  solutionProvider?: string
): Record<string, string> {
  const changeKey = getMouseChangeKeyLabels(configData);
  const result: Record<string, string> = {};
  for (const buttonId of MOUSE_BUTTON_IDS) {
    const resolved = resolveMouseButtonDisplayLabel(
      buttonId,
      changeKey[buttonId],
      configData,
      t,
      solutionProvider
    );
    result[buttonId] = resolved ?? labels[buttonId];
  }
  return result;
}

const MOUSE_BUTTON_IDS = ["left", "right", "middle", "side1", "side2"] as const;

const MOUSE_BUTTON_INDEX: Record<(typeof MOUSE_BUTTON_IDS)[number], number> = {
  left: 0,
  right: 1,
  middle: 2,
  side1: 3,
  side2: 4,
};

function getMouseKeyRawValueFromSnapshot(
  configData: Record<string, unknown> | undefined,
  buttonId: (typeof MOUSE_BUTTON_IDS)[number]
): unknown {
  const changeKeyRaw = configData?.changeKeyRaw;
  if (
    changeKeyRaw &&
    typeof changeKeyRaw === "object" &&
    !Array.isArray(changeKeyRaw)
  ) {
    const raw = (changeKeyRaw as Record<string, unknown>)[buttonId];
    if (raw != null) return raw;
  }

  const snapshot = configData?._boardSnapshot;
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
    return undefined;
  }
  const snap = snapshot as Record<string, unknown>;
  const index = MOUSE_BUTTON_INDEX[buttonId];

  if (snap.format === "jiangmeng-board-v1") {
    const config = snap.config as {
      keys?: Array<{ index: number; value: unknown }>;
    };
    const keys = config?.keys;
    if (!Array.isArray(keys)) return undefined;
    return (
      keys.find((item) => item.index === index)?.value ?? keys[index]?.value
    );
  }

  if (snap.format === "beiying-board-v1") {
    const config = snap.config as {
      keys?: Array<{ value?: unknown }>;
    };
    const keys = config?.keys;
    if (Array.isArray(keys) && keys[index]?.value != null) {
      return keys[index]?.value;
    }
  }

  if (snap.format === "zhanli-board-bin-v1") {
    // 二进制快照不解析按键细节
    return undefined;
  }

  const cfg = snap.config as { keys?: Array<{ value?: unknown }> } | undefined;
  const keys = cfg?.keys;
  if (Array.isArray(keys) && keys[index]?.value != null) {
    return keys[index]?.value;
  }
  return undefined;
}

function resolveMouseButtonDisplayLabel(
  buttonId: (typeof MOUSE_BUTTON_IDS)[number],
  label: string | undefined,
  configData: Record<string, unknown> | undefined,
  t?: TFunction,
  solutionProvider?: string
): string | undefined {
  const rawValue = getMouseKeyRawValueFromSnapshot(configData, buttonId);
  if (solutionProvider === "zhanli") {
    if (Array.isArray(rawValue) && rawValue.length > 0 && t) {
      const macros = readZhanliMacrosFromBoardSnapshot(
        configData?._boardSnapshot
      );
      const macroNameResolver = macros
        ? buildZhanliMacroNameResolver(macros)
        : undefined;
      const formatted = resolveZhanliMouseKeyLabel(
        t,
        rawValue as (string | number)[],
        macroNameResolver ? { macroNameResolver } : undefined
      );
      if (formatted && formatted !== t("mouse.changeKey.notSet", "未设置")) {
        return formatted;
      }
    }
    if (label) return label;
    return undefined;
  }
  return normalizeMouseChangeKeyDisplayLabel(label, rawValue, t);
}

/** 改键预览：映射与默认标签不同的按键 id */
export function getMouseRemappedButtonIds(
  buttonMappings: Record<string, string>,
  defaultLabels: Record<string, string>
): string[] {
  return MOUSE_BUTTON_IDS.filter((id) => {
    const mapped = String(buttonMappings[id] ?? "").trim();
    const fallback = String(defaultLabels[id] ?? "").trim();
    return mapped.length > 0 && mapped !== fallback;
  });
}

const REPORT_RATE_BY_HZ = [125, 250, 500, 1000, 2000, 4000, 8000];

function resolveReportRateIndex(perf?: Record<string, unknown>): number {
  if (perf?.reportRate != null) {
    return Number(perf.reportRate);
  }
  if (perf?.pollingRate != null) {
    const hz = Number(perf.pollingRate);
    const exact = REPORT_RATE_BY_HZ.indexOf(hz);
    if (exact >= 0) return exact;
    let closest = 0;
    let minDiff = Infinity;
    REPORT_RATE_BY_HZ.forEach((rate, index) => {
      const diff = Math.abs(rate - hz);
      if (diff < minDiff) {
        minDiff = diff;
        closest = index;
      }
    });
    return closest;
  }
  return 0;
}

/** 匠盟传感器模式：EEPROM 2 与 UI 256 均表示狂暴 */
export function normalizeMouseSensorMode(value: unknown): 0 | 1 | 256 {
  const n = Number(value);
  if (n === 1) return 1;
  if (n === 256 || n === 2) return 256;
  return 0;
}

function resolveMouseSensorModeFromPayload(
  configData?: Record<string, unknown>
): 0 | 1 | 256 {
  const perf = asRecord(configData?.performance);
  const sensor = asRecord(configData?.sensor);
  const root = resolveMouseCfgRoot(configData);

  const candidates = [root.sensorMode, sensor?.sensorMode, perf?.sensorMode];

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && candidate !== "") {
      return normalizeMouseSensorMode(candidate);
    }
  }
  return 0;
}

/** 贝盈工作模式：0 办公 / 1 高速 / 2 游戏 / 3 狂暴（勿与匠盟 sensorMode 混用） */
export function resolveMouseWorkModeFromPayload(
  configData?: Record<string, unknown>
): number {
  const perf = asRecord(configData?.performance);
  const sensor = asRecord(configData?.sensor);
  const root = resolveMouseCfgRoot(configData);

  for (const candidate of [perf?.workMode, sensor?.workMode, root.workMode]) {
    if (candidate !== undefined && candidate !== null && candidate !== "") {
      const n = Number(candidate);
      if (Number.isFinite(n)) return n;
    }
  }

  const snapshot = configData?._boardSnapshot;
  if (snapshot && typeof snapshot === "object") {
    const snap = snapshot as Record<string, unknown>;
    if (snap.format === "beiying-board-v1") {
      const cfg = asRecord(snap.config);
      const cfgPerf = asRecord(cfg?.performance);
      const cfgSensor = asRecord(cfg?.sensor);
      for (const candidate of [cfgPerf?.workMode, cfgSensor?.workMode]) {
        if (candidate !== undefined && candidate !== null && candidate !== "") {
          const n = Number(candidate);
          if (Number.isFinite(n)) return n;
        }
      }
    }
  }

  return 1;
}

export function getMousePerformanceValues(
  configData?: Record<string, unknown>
): MousePerformancePreviewValues {
  const perf = asRecord(configData?.performance);
  const reportRate = resolveReportRateIndex(perf);
  const sensorMode = resolveMouseSensorModeFromPayload(configData);
  const workMode = resolveMouseWorkModeFromPayload(configData);
  return { reportRate, sensorMode, workMode };
}

export interface ZhanliPerformancePreviewValues {
  reportRate: number;
  /** 展立协议模式：1/2/3/4/5/6 */
  protocolMode: number;
}

/** 展立性能预览：保留协议 sensorMode，不压成匠盟 0|1|256 */
export function getZhanliPerformancePreviewValues(
  configData?: Record<string, unknown>
): ZhanliPerformancePreviewValues {
  const perf = asRecord(configData?.performance);
  const sensor = asRecord(configData?.sensor);
  const root = resolveMouseCfgRoot(configData);
  const rootSensor = asRecord(root.sensor);

  const hzOrIndex = Number(
    root.reportRate ?? perf?.pollingRate ?? perf?.reportRate ?? 1000
  );
  let reportRate: number;
  if (REPORT_RATE_BY_HZ.includes(hzOrIndex)) {
    reportRate = REPORT_RATE_BY_HZ.indexOf(hzOrIndex);
  } else if (hzOrIndex >= 0 && hzOrIndex <= 6) {
    reportRate = hzOrIndex;
  } else {
    reportRate = resolveReportRateIndex({ pollingRate: hzOrIndex });
  }

  const protocolMode =
    Number(
      sensor?.sensorMode ??
        rootSensor?.sensorMode ??
        perf?.sensorMode ??
        root.sensorMode ??
        2
    ) || 2;

  return { reportRate, protocolMode };
}

/** 顶层 dpi 数值（旧 mock 格式） */
export function resolveLegacyDpi(
  configData?: Record<string, unknown>
): number | undefined {
  if (typeof configData?.dpi === "number") {
    return configData.dpi;
  }
  return undefined;
}

export interface MouseReceiverLightingPreviewValues {
  mode: number;
  brightness: number;
  speed: number;
  colorHex: string;
  colorDisabled: boolean;
  brightnessDisabled: boolean;
  speedDisabled: boolean;
}

export interface MouseDpiLightingPreviewValues {
  mode: "off" | "static" | "breathing";
  brightness: number;
  speed: number;
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function parseColorHex(color: unknown, fallback = "#239894"): string {
  if (typeof color === "string") {
    const trimmed = color.trim();
    if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed;
    const rgbMatch = trimmed.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      return rgbToHex(
        Number(rgbMatch[1]),
        Number(rgbMatch[2]),
        Number(rgbMatch[3])
      );
    }
  }
  const rgb = asRecord(color);
  if (rgb && "r" in rgb) {
    return rgbToHex(Number(rgb.r ?? 0), Number(rgb.g ?? 0), Number(rgb.b ?? 0));
  }
  return fallback;
}

/** 与主页 `dongleLightCapability.lightModeToDisable` 对齐 */
function receiverLightModeDisables(
  mode: number,
  scheme: "jiangmeng" | "zhanli"
): {
  color: boolean;
  brightness: boolean;
  speed: boolean;
} {
  if (scheme === "zhanli") {
    // 展立：2=单色常亮 3=单色呼吸
    switch (mode) {
      case 0:
        return { color: true, brightness: true, speed: true };
      case 1:
      case 4:
      case 5:
        return { color: true, brightness: false, speed: false };
      case 6:
        return { color: true, brightness: false, speed: true };
      case 2:
        return { color: false, brightness: false, speed: true };
      default:
        return { color: false, brightness: false, speed: false };
    }
  }

  // 匠盟：2=单色呼吸 3=单色常亮
  switch (mode) {
    case 0:
      return { color: true, brightness: true, speed: true };
    case 1:
    case 4:
    case 5:
      return { color: true, brightness: false, speed: false };
    case 6:
      return { color: true, brightness: false, speed: true };
    case 3:
      return { color: false, brightness: false, speed: true };
    case 2:
    default:
      return { color: false, brightness: false, speed: false };
  }
}

function clampReceiverPercent(value: unknown, fallback = 100): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(10, Math.round(n / 10) * 10));
}

function clampZhanliReceiverPercent(value: unknown, fallback = 100): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.trunc(n)));
}

function resolveMouseCfgRoot(
  configData?: Record<string, unknown>
): Record<string, unknown> {
  const snapshot = configData?._boardSnapshot;
  if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) {
    const snap = snapshot as Record<string, unknown>;
    if (snap.format === "jiangmeng-board-v1" && snap.config) {
      return snap.config as Record<string, unknown>;
    }
    if (snap.format === "beiying-board-v1" && snap.config) {
      return snap.config as Record<string, unknown>;
    }
    if (snap.format === "zhanli-board-bin-v1") {
      return configData ?? {};
    }
    const nested =
      asRecord(snap.config) ?? asRecord(snap.cfg) ?? asRecord(snap.mouseCfg);
    return nested ?? snap;
  }
  if (Array.isArray(snapshot) && snapshot.length > 0) {
    return configData ?? {};
  }
  return configData ?? {};
}

export interface BeiyingMouseParameterPreviewValues {
  motionSync: boolean;
  ripple: boolean;
  angle: boolean;
  lod: number;
  sleepLevel: number;
  neverSleep: boolean;
  debounceTime: number;
}

export interface ZhanliMouseParameterPreviewValues {
  motionSync: boolean;
  ripple: boolean;
  angle: boolean;
  lod: number;
  sleepMinutes: number;
  debounceTime: number;
  angleTune: number;
  virtualOffsetEnable: boolean;
  virtualOffsetUi: number;
}

/** 展立参数预览（sleep 0–30 分钟，debounce 0–255ms；不展示移动唤醒，对齐参数页） */
export function getZhanliMouseParameterPreviewValues(
  configData?: Record<string, unknown>
): ZhanliMouseParameterPreviewValues {
  const root = resolveMouseCfgRoot(configData);
  const perf =
    asRecord(configData?.performance) ?? asRecord(root.performance) ?? {};
  const sensor = asRecord(configData?.sensor) ?? asRecord(root.sensor) ?? {};

  const sleepRaw = Number(
    configData?.sleepTime ?? perf.sleepTime ?? root.sleepTime ?? 3
  );
  const debounceRaw =
    configData?.debounceTime ?? perf.debounceTime ?? root.debounceTime ?? 8;
  const lodRaw = configData?.lod ?? perf.lod ?? sensor.lod ?? root.lod ?? 2;
  const angleTuneRaw =
    configData?.sensorAngle ??
    sensor.angleTune ??
    (root as { sensorAngle?: number }).sensorAngle ??
    0;
  const virtualOffsetUiRaw = Number(sensor.virtualOffsetUi ?? 0);

  return {
    motionSync: Boolean(
      sensor.motionSync ?? perf.motionSync ?? root.motionSync
    ),
    ripple: Boolean(sensor.ripple ?? perf.ripple ?? root.ripple),
    angle: Boolean(sensor.angle ?? perf.angle ?? root.angle),
    lod: Number(lodRaw),
    sleepMinutes: Math.max(0, Math.min(30, Math.trunc(sleepRaw) || 0)),
    debounceTime: Math.max(
      0,
      Math.min(255, Math.floor(Number(debounceRaw)) || 0)
    ),
    angleTune: Number(angleTuneRaw),
    virtualOffsetEnable: Boolean(sensor.virtualOffsetEnable),
    virtualOffsetUi: Math.max(
      -100,
      Math.min(100, Math.trunc(virtualOffsetUiRaw) || 0)
    ),
  };
}

/** 贝盈鼠标参数预览（sleep 为档位 ×30s，debounce 0–20ms） */
export function getBeiyingMouseParameterPreviewValues(
  configData?: Record<string, unknown>
): BeiyingMouseParameterPreviewValues {
  const root = resolveMouseCfgRoot(configData);
  const perf =
    asRecord(configData?.performance) ?? asRecord(root.performance) ?? {};
  const sensor = asRecord(configData?.sensor) ?? {};

  const sleepLevel = Number(
    configData?.sleepTime ?? perf.sleepTime ?? root.sleepTime ?? 1
  );
  const debounceRaw =
    configData?.debounceTime ?? perf.debounceTime ?? root.debounceTime ?? 5;

  return {
    motionSync: Boolean(
      sensor.motionSync ?? perf.motionSync ?? root.motionSync
    ),
    ripple: Boolean(sensor.ripple ?? perf.ripple ?? root.ripple),
    angle: Boolean(sensor.angle ?? perf.angle ?? root.angle),
    lod: Number(configData?.lod ?? perf.lod ?? sensor.lod ?? root.lod ?? 1),
    sleepLevel,
    neverSleep: sleepLevel === 0,
    debounceTime: Math.max(0, Math.min(20, Math.floor(Number(debounceRaw)))),
  };
}

export interface MouseParameterPreviewValues {
  motionSync: boolean;
  ripple: boolean;
  angle: boolean;
  longDistance: boolean;
  fps20k: boolean;
  angleTune: number;
  lod: number;
  sleepTime: number;
  debounceTime: number;
}

export function getMouseParameterPreviewValues(
  configData?: Record<string, unknown>
): MouseParameterPreviewValues {
  const root = resolveMouseCfgRoot(configData);
  const perf = asRecord(configData?.performance) ?? {};
  const sensor = asRecord(configData?.sensor) ?? perf;

  const debounceRaw =
    configData?.debounceTime ?? perf.debounceTime ?? root.performance ?? 8;
  const sleepRaw =
    configData?.sleepTime ?? perf.sleepTime ?? root.sleepTime ?? 6;
  const lodRaw =
    configData?.lod ?? perf.lod ?? sensor.lod ?? root.lod ?? perf.freqLod ?? 1;
  const sensorAngleRaw =
    configData?.sensorAngle ??
    (root as { sensorAngle?: number }).sensorAngle ??
    sensor.angleTune;

  return {
    motionSync: Boolean(sensor.motionSync ?? root.motionSync),
    ripple: Boolean(sensor.ripple ?? root.ripple),
    angle: Boolean(sensor.angle ?? perf.angle ?? root.angle),
    longDistance: Boolean(configData?.longDistance ?? root.longDistance),
    fps20k: Boolean(sensor.fps20k ?? root.fps20k),
    angleTune: Number(
      sensorAngleRaw ?? sensor.angleTune ?? root.angleTune ?? 0
    ),
    lod: Number(lodRaw),
    sleepTime: Number(sleepRaw),
    debounceTime: Math.max(0, Math.min(15, Math.floor(Number(debounceRaw)))),
  };
}

export function getMouseReceiverLightingPreview(
  configData?: Record<string, unknown>,
  solutionProvider?: string
): MouseReceiverLightingPreviewValues {
  const scheme =
    String(solutionProvider || "").toLowerCase() === "zhanli"
      ? "zhanli"
      : "jiangmeng";
  const root = resolveMouseCfgRoot(configData);
  const lighting = asRecord(configData?.lighting);
  const receiver =
    asRecord(lighting?.receiver) ??
    asRecord(root.dongleRGBBar) ??
    asRecord(configData?.dongleRGBBar);

  // 与主页 DEFAULT_DONGLE_RGB_BAR 一致：缺省为彩色流动
  const mode = Number(receiver?.mode ?? 1);
  const brightnessRaw = Number(
    receiver?.brightness ?? (scheme === "zhanli" ? 100 : 9)
  );
  const speedRaw = Number(receiver?.speed ?? (scheme === "zhanli" ? 100 : 8));
  const disables = receiverLightModeDisables(mode, scheme);

  // 展立协议 0–100；匠盟为设备档 0–9
  const brightness =
    scheme === "zhanli"
      ? clampZhanliReceiverPercent(brightnessRaw)
      : clampReceiverPercent((brightnessRaw + 1) * 10);
  const speed =
    scheme === "zhanli"
      ? clampZhanliReceiverPercent(speedRaw)
      : clampReceiverPercent((speedRaw + 1) * 10);

  return {
    mode,
    brightness,
    speed,
    colorHex: parseColorHex(receiver?.color, "#ff0000"),
    colorDisabled: disables.color,
    brightnessDisabled: disables.brightness,
    speedDisabled: disables.speed,
  };
}

export function getMouseDpiLightingPreview(
  configData?: Record<string, unknown>
): MouseDpiLightingPreviewValues {
  const root = resolveMouseCfgRoot(configData);
  const lighting = asRecord(configData?.lighting);
  const dpi =
    asRecord(lighting?.dpi) ??
    asRecord(root.dpiEffect) ??
    asRecord(configData?.dpiEffect);

  const state = dpi?.state ?? dpi?.show;
  const modeNum = Number(dpi?.mode ?? 0);
  let mode: MouseDpiLightingPreviewValues["mode"] = "off";
  if (state !== false && state !== 0) {
    if (modeNum === 1) mode = "static";
    else if (modeNum === 2) mode = "breathing";
  }

  const brightnessRaw = Number(dpi?.brightness ?? 5);
  const speedRaw = Number(dpi?.speed ?? 3);

  return {
    mode,
    brightness: Math.min(100, Math.max(10, brightnessRaw * 10)),
    speed: Math.min(100, Math.max(20, speedRaw * 20)),
  };
}

export type DynamicPointPreview = [number, number];

export interface MouseDynamicSensitivityPreviewValues {
  enable: boolean;
  mode: 0 | 1 | 2 | 3;
  points: DynamicPointPreview[];
}

function asDynamicPoints(raw: unknown): DynamicPointPreview[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const points = raw
    .map((item) => {
      if (!Array.isArray(item) || item.length < 2) return null;
      const speed = Number(item[0]);
      const gain = Number(item[1]);
      if (!Number.isFinite(speed) || !Number.isFinite(gain)) return null;
      return [speed, gain] as DynamicPointPreview;
    })
    .filter((item): item is DynamicPointPreview => item != null);
  return points.length ? points : null;
}

/** 社区预览：从 payload / 板载快照解析动态灵敏（不含实时遥测） */
export function getMouseDynamicSensitivityPreviewValues(
  configData?: Record<string, unknown>
): MouseDynamicSensitivityPreviewValues {
  const root = resolveMouseCfgRoot(configData);
  const fromPayload = asRecord(configData?.dynamicSensitivity);
  const snapshot = configData?._boardSnapshot;
  const snapCfg =
    snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)
      ? asRecord((snapshot as { config?: unknown }).config)
      : undefined;
  const fromSnap = asRecord(snapCfg?.dynamicSensitivity);
  const dyn =
    fromPayload ?? fromSnap ?? asRecord(root.dynamicSensitivity) ?? {};

  const modeRaw = Number(dyn.mode ?? 0);
  const mode = (
    modeRaw === 1 || modeRaw === 2 || modeRaw === 3 ? modeRaw : 0
  ) as 0 | 1 | 2 | 3;

  const customPoints =
    asDynamicPoints(dyn.customPoints) ??
    asDynamicPoints(fromSnap?.customPoints);
  const points =
    (mode === 3
      ? (customPoints ?? asDynamicPoints(dyn.points))
      : asDynamicPoints(dyn.points)) ??
    customPoints ??
    ([
      [0, 1],
      [25, 1.1],
      [50, 1.2],
      [75, 1.35],
      [100, 1.5],
    ] as DynamicPointPreview[]);

  return {
    enable: Number(dyn.enable ?? 0) === 1 || dyn.enable === true,
    mode,
    points: points.slice(0, 5),
  };
}
